export type ReorderPlacement = {
  position: 'before' | 'after';
  anchorId: string;
};

export type ReorderAnchorPair = {
  beforeId: string | null;
  afterId: string | null;
};

type ReorderableItem = {
  id: string;
};

export function getReorderPlacement<T extends ReorderableItem>(
  items: readonly T[],
  activeId: string,
  overId: string
): ReorderPlacement | null {
  if (activeId === overId) return null;

  const oldIndex = items.findIndex((item) => item.id === activeId);
  const newIndex = items.findIndex((item) => item.id === overId);
  if (oldIndex < 0 || newIndex < 0) return null;

  return oldIndex < newIndex
    ? { position: 'after', anchorId: overId }
    : { position: 'before', anchorId: overId };
}

/**
 * Collapse a placement to the single anchor on the drop side (`after` → `beforeId`, `before` →
 * `afterId`) and leave the opposite neighbour null. Only correct for move RPCs that re-derive the
 * slot from that one anchor against the current row order. RPCs that insert at the MIDPOINT between
 * two neighbours — e.g. `us_move_node` / `us_calc_insert_position` — need both, or they fall back to
 * `anchor ± fixed gap`, collide with the existing sibling, and reject the move; use
 * `getReorderAnchorPair` for those.
 */
export function placementToAnchorPair(placement: ReorderPlacement): ReorderAnchorPair {
  return placement.position === 'after'
    ? { beforeId: placement.anchorId, afterId: null }
    : { beforeId: null, afterId: placement.anchorId };
}

export function getReorderAnchorPair<T extends ReorderableItem>(
  items: readonly T[],
  activeId: string,
  placement: ReorderPlacement
): ReorderAnchorPair | null {
  if (activeId === placement.anchorId) return null;

  const oldIndex = items.findIndex((item) => item.id === activeId);
  const anchorIndex = items.findIndex((item) => item.id === placement.anchorId);
  if (oldIndex < 0 || anchorIndex < 0) return null;

  const reordered = reorderByPlacement(items, activeId, placement);
  const movedIndex = reordered.findIndex((item) => item.id === activeId);
  if (movedIndex < 0) return null;

  return {
    beforeId: reordered[movedIndex - 1]?.id ?? null,
    afterId: reordered[movedIndex + 1]?.id ?? null,
  };
}

export function reorderByPlacement<T extends ReorderableItem>(
  items: readonly T[],
  activeId: string,
  placement: ReorderPlacement
): T[] {
  if (activeId === placement.anchorId) return [...items];

  const oldIndex = items.findIndex((item) => item.id === activeId);
  const anchorIndex = items.findIndex((item) => item.id === placement.anchorId);
  if (oldIndex < 0 || anchorIndex < 0) return [...items];

  const next = [...items];
  const [moved] = next.splice(oldIndex, 1);
  if (!moved) return [...items];

  const anchorIndexAfterRemoval = next.findIndex((item) => item.id === placement.anchorId);
  if (anchorIndexAfterRemoval < 0) return [...items];

  const insertIndex =
    placement.position === 'before' ? anchorIndexAfterRemoval : anchorIndexAfterRemoval + 1;
  next.splice(insertIndex, 0, moved);
  return next;
}

export function reindexLessonBlocks<T extends { lesson_order: number }>(blocks: readonly T[]): T[] {
  return blocks.map((block, index) => ({
    ...block,
    lesson_order: index + 1,
  }));
}
